import { useMemo, useState } from "react";
import { FormModalControlled } from "../../../components/Modal";
import DropdownSelector from "../../../components/DropdownSelector";
import Input from "../../../components/Input";
import Label from "../../../components/Label";
import { ToggleChip } from "../../../components/Chip";

// IF token types
const IF_TOKEN_TYPES = [
  { id: "PARAMETER", label: "Parametras" },
  { id: "VALUE", label: "Reikšmė" },
  { id: "OPERATOR", label: "Palyginimas" },
  { id: "LOGIC", label: "Loginis operatorius" },
];

// THEN token types
const THEN_TOKEN_TYPES = [
  { id: "CONSTANT", label: "Kaina" },
  { id: "PARAMETER", label: "Parametras" },
  { id: "OPERATOR", label: "Operatorius" },
  { id: "BRACKET", label: "Skliaustai" },
  { id: "NUMBER", label: "Skaičius" },
];

const IF_OPERATORS = [
  { id: "==", label: "==" },
  { id: "!=", label: "!=" },
];

const LOGIC_OPERATORS = [
  { id: "ARBA", label: "ARBA" },
  { id: "IR", label: "IR" },
];

const THEN_OPERATORS = [
  { id: "+", label: "+" },
  { id: "-", label: "-" },
  { id: "*", label: "*" },
  { id: "/", label: "/" },
];

const BRACKET_OPTIONS = [
  { id: "(", label: "(" },
  { id: ")", label: ")" },
];

const PRICE_OPTION = { id: "Kaina", label: "Kaina" };

const AddConditionElementModal = ({
  isOpen,
  onClose,
  onAdd,
  parameters = [],
}) => {
  const [target, setTarget] = useState("IF"); // "IF" or "THEN"
  const [type, setType] = useState(null);
  const [selectedValue, setSelectedValue] = useState(null);
  const [numberValue, setNumberValue] = useState("");

  const tokenTypes = target === "IF" ? IF_TOKEN_TYPES : THEN_TOKEN_TYPES;

  const effectiveType = type || tokenTypes[0];

  // Build parameter options from config parameters
  const parameterOptions = useMemo(() => {
    return parameters
      .filter((p) => p.id && p.values?.length > 0)
      .map((p) => ({ id: p.label || p.id, label: p.label || p.id }));
  }, [parameters]);

  // Build value options — all values from all parameters
  const valueOptions = useMemo(() => {
    const values = [];
    parameters.forEach((p) => {
      (p.values || []).forEach((v) => {
        const label = typeof v === "number" ? `${v * 10} mm` : String(v);
        values.push({ id: String(v), label });
      });
    });
    return values;
  }, [parameters]);

  const dropdownOptions = useMemo(() => {
    if (target === "IF") {
      switch (effectiveType?.id) {
        case "PARAMETER":
          return parameterOptions;
        case "VALUE":
          return valueOptions;
        case "OPERATOR":
          return IF_OPERATORS;
        case "LOGIC":
          return LOGIC_OPERATORS;
        default:
          return [];
      }
    } else {
      switch (effectiveType?.id) {
        case "CONSTANT":
          return [PRICE_OPTION];
        case "PARAMETER":
          return parameterOptions;
        case "OPERATOR":
          return THEN_OPERATORS;
        case "BRACKET":
          return BRACKET_OPTIONS;
        default:
          return [];
      }
    }
  }, [target, effectiveType, parameterOptions, valueOptions]);

  const effectiveValue = selectedValue ?? dropdownOptions?.[0] ?? null;
  const isNumberType = target === "THEN" && effectiveType?.id === "NUMBER";
  const isPriceType = target === "THEN" && effectiveType?.id === "CONSTANT";

  const canSubmit = isNumberType
    ? numberValue.trim() !== "" && !Number.isNaN(Number(numberValue.trim()))
    : effectiveValue?.id != null;

  const resetState = () => {
    setType(null);
    setSelectedValue(null);
    setNumberValue("");
  };

  const handleClose = () => {
    resetState();
    onClose?.();
  };

  const handleSubmit = () => {
    if (!canSubmit) return;

    let token;
    if (isNumberType) {
      token = { type: "NUMBER", value: numberValue.trim() };
    } else {
      token = { type: effectiveType.id, value: effectiveValue.id };
    }

    onAdd?.(token, target);
    handleClose();
  };

  return (
    <FormModalControlled
      isOpen={isOpen}
      onClose={handleClose}
      onCancel={handleClose}
      onSubmit={handleSubmit}
      submitLabel="Pridėti"
      cancelLabel="Atšaukti"
      disabled={!canSubmit}
    >
      <h2>Pridėti sąlygos elementą</h2>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--gap)",
          padding: "var(--gap) 0",
          minWidth: "min(480px, 90vw)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--gap)",
          }}
        >
          <div style={{ width: "var(--width-20)" }}>
            <Label>Dalis</Label>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--gap-sm)",
            }}
          >
            <ToggleChip
              active={target === "IF"}
              onToggle={() => { setTarget("IF"); resetState(); }}
            >
              JEI
            </ToggleChip>
            <ToggleChip
              active={target === "THEN"}
              onToggle={() => { setTarget("THEN"); resetState(); }}
            >
              TAI
            </ToggleChip>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--gap)",
          }}
        >
          <div style={{ width: "var(--width-20)" }}>
            <Label>Tipas</Label>
          </div>
          <DropdownSelector
            options={tokenTypes}
            value={effectiveType}
            noEmpty
            onChange={(next) => {
              setType(next);
              setSelectedValue(null);
              setNumberValue("");
            }}
          />
        </div>

        {isPriceType ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--gap)",
            }}
          >
            <div style={{ width: "var(--width-20)" }}>
              <Label>Reikšmė</Label>
            </div>
            <div style={{ opacity: 0.5 }}>Kaina</div>
          </div>
        ) : isNumberType ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--gap)",
            }}
          >
            <div style={{ width: "var(--width-20)" }}>
              <Label>Skaičius</Label>
            </div>
            <Input
              type="number"
              value={numberValue}
              onChange={(e) => setNumberValue(e.currentTarget.value)}
              placeholder="Pvz. 30"
            />
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--gap)",
            }}
          >
            <div style={{ width: "var(--width-20)" }}>
              <Label>Reikšmė</Label>
            </div>
            {dropdownOptions.length > 0 ? (
              <DropdownSelector
                options={dropdownOptions}
                value={selectedValue}
                noEmpty
                onChange={(next) => setSelectedValue(next)}
                defaultValue={dropdownOptions?.[0]}
              />
            ) : (
              <div style={{ opacity: 0.5 }}>
                {effectiveType?.id === "PARAMETER"
                  ? "Nėra pridėtų parametrų"
                  : effectiveType?.id === "VALUE"
                  ? "Nėra pridėtų reikšmių"
                  : "—"}
              </div>
            )}
          </div>
        )}
      </div>
    </FormModalControlled>
  );
};

export default AddConditionElementModal;
